import { t, type Idioma } from '@matias-asset-os/i18n';
import { fechaLarga } from '../componentes.js';
import { Icono } from '../componentes/Iconos.js';
import type { Obra } from './modelo.js';
import { Dinero, Frescura, PastillaEtapa, Progreso, ProximaAccion, Puntuacion } from './piezas.jsx';

/**
 * Lo que se ve al tocar un marcador: lo justo para decidir si merece abrirla.
 *
 * Doce datos y un boton. Si aqui cupiera todo, la ficha completa sobraria; si
 * cupiera menos, habria que abrir cada obra para descartarla, que es lo que
 * hace inutil un mapa con veinte marcadores.
 *
 * En escritorio es un panel lateral; en movil, la hoja de estilo lo convierte
 * en un panel inferior, que es donde llega el pulgar.
 */

interface Props {
  obra: Obra;
  idioma: Idioma;
  hoy: string;
  onCerrar: () => void;
  onAbrir: (id: string) => void;
}

function promotorDe(obra: Obra): string | null {
  const c =
    obra.contactos.find((x) => x.rol === 'PROPIETARIO') ??
    obra.contactos.find((x) => x.rol === 'PROMOTOR') ??
    obra.contactos.find((x) => x.rol === 'COMERCIALIZADORA');
  return c?.nombre ?? null;
}

export function FichaRapida({ obra, idioma, hoy, onCerrar, onAbrir }: Props) {
  const promotor = promotorDe(obra);

  return (
    <aside className="obra-ficha" aria-label={obra.nombre}>
      <div className="obra-ficha-cabeza">
        <div className="crecer">
          <span className="rotulo">
            {obra.codigo} · {t(idioma, `obras.tipo.${obra.tipoProyecto}`)}
          </span>
          <h3>{obra.nombre}</h3>
          <p className="suave">
            <Icono nombre="pin" /> {obra.zona} · {obra.municipio}
          </p>
        </div>
        <button
          type="button"
          className="modal-cerrar"
          aria-label={t(idioma, 'obras.cerrarFicha')}
          onClick={onCerrar}
        >
          <Icono nombre="cerrar" />
        </button>
      </div>

      <div className="obra-ficha-fila">
        <PastillaEtapa etapa={obra.etapa} idioma={idioma} />
        <Puntuacion obra={obra} idioma={idioma} hoy={hoy} />
        <Frescura obra={obra} idioma={idioma} hoy={hoy} />
      </div>

      <dl className="obra-datos">
        <dt>{t(idioma, 'obras.progreso')}</dt>
        <dd>
          <Progreso obra={obra} idioma={idioma} />
        </dd>

        <dt>{t(idioma, 'obras.fase')}</dt>
        <dd>{t(idioma, `obras.fase.${obra.fase}`)}</dd>

        <dt>{t(idioma, 'obras.finEstimada')}</dt>
        <dd>{fechaLarga(obra.finalizacionEstimada, idioma)}</dd>

        <dt>{t(idioma, 'obras.responsable')}</dt>
        <dd>{obra.responsable ?? <span className="suave">{t(idioma, 'obras.sinResponsable')}</span>}</dd>

        <dt>{t(idioma, 'obras.ultimaComprobacion')}</dt>
        <dd>{fechaLarga(obra.ultimaComprobacion, idioma)}</dd>

        <dt>{t(idioma, 'obras.promotorPropietario')}</dt>
        <dd>{promotor ?? <span className="suave">{t(idioma, 'obras.sinDato')}</span>}</dd>

        <dt>{t(idioma, 'obras.valorMercado')}</dt>
        <dd>
          <Dinero cents={obra.valorMercadoCents} idioma={idioma} />
        </dd>
      </dl>

      <div className="obra-ficha-accion">
        <span className="rotulo">{t(idioma, 'obras.proximaAccion')}</span>
        <ProximaAccion obra={obra} idioma={idioma} hoy={hoy} detallada />
      </div>

      <button type="button" onClick={() => onAbrir(obra.id)}>
        {t(idioma, 'obras.abrir')}
      </button>
    </aside>
  );
}
